/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Memoized section field/validation handlers for registration form.
 * @module hooks/useAppointmentRegistrationSectionHandlers
 */

import { useCallback } from "react";

import { logger } from "../utils/logger.js";

/**
 * Builds stable per-section onChange/onValidation callbacks for registration sections.
 * @param {function(string, string, *): void} handleFieldChange
 * @returns {Object}
 */
const useAppointmentRegistrationSectionHandlers = (handleFieldChange) => {
  const handleValidationChange = useCallback((_section, _hasErrors) => {
    logger.info("Validation change detected", {
      data: { section: _section, hasErrors: _hasErrors },
    });
  }, []);

  const handleCorporateIdentityFieldChange = useCallback(
    (field, value) => handleFieldChange("informacionGeneral", field, value),
    [handleFieldChange],
  );
  const handleContactInformationFieldChange = useCallback(
    (field, value) => handleFieldChange("informacionGeneral", field, value),
    [handleFieldChange],
  );
  const handleLocationFieldChange = useCallback(
    (field, value) => handleFieldChange("contactoUbicacion", field, value),
    [handleFieldChange],
  );
  const handleCommercialOfferFieldChange = useCallback(
    (field, value) => handleFieldChange("productosServicios", field, value),
    [handleFieldChange],
  );
  const handleDocumentsFieldChange = useCallback(
    (field, value) => handleFieldChange("vinculacionNetworking", field, value),
    [handleFieldChange],
  );
  const handleCertificationsFieldChange = useCallback(
    (field, value) => handleFieldChange("vinculacionNetworking", field, value),
    [handleFieldChange],
  );

  const handleCorporateIdentityValidationChange = useCallback(
    (hasErrors) => handleValidationChange("corporateIdentity", hasErrors),
    [handleValidationChange],
  );
  const handleContactInformationValidationChange = useCallback(
    (hasErrors) => handleValidationChange("contactInformation", hasErrors),
    [handleValidationChange],
  );
  const handleLocationValidationChange = useCallback(
    (hasErrors) => handleValidationChange("location", hasErrors),
    [handleValidationChange],
  );
  const handleCommercialOfferValidationChange = useCallback(
    (hasErrors) => handleValidationChange("commercialOffer", hasErrors),
    [handleValidationChange],
  );
  const handleDocumentsValidationChange = useCallback(
    (hasErrors) => handleValidationChange("documents", hasErrors),
    [handleValidationChange],
  );
  const handleCertificationsValidationChange = useCallback(
    (hasErrors) => handleValidationChange("certifications", hasErrors),
    [handleValidationChange],
  );

  return {
    handleCorporateIdentityFieldChange,
    handleContactInformationFieldChange,
    handleLocationFieldChange,
    handleCommercialOfferFieldChange,
    handleDocumentsFieldChange,
    handleCertificationsFieldChange,
    handleCorporateIdentityValidationChange,
    handleContactInformationValidationChange,
    handleLocationValidationChange,
    handleCommercialOfferValidationChange,
    handleDocumentsValidationChange,
    handleCertificationsValidationChange,
  };
};

export default useAppointmentRegistrationSectionHandlers;
